import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, LogIn, Shield } from 'lucide-react';
import { useTechAuth } from '@/contexts/TechAuthContext';
import { getPortalContext } from '@/lib/portal';
import { getTechBranding } from '@/lib/tech-branding';
import { supabase, isSupabaseConfigured, setAuthStorageMode } from '@/lib/supabase';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error || 'Unknown error');
}

export default function TechLoginPage() {
  const [mode, setMode] = useState<'login' | 'forgot'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [logoError, setLogoError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const { signIn } = useTechAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setSubmitting(true);

    try {
      setAuthStorageMode(rememberDevice ? 'local' : 'session');
      await signIn(email, password);
      navigate('/dashboard');
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  const portal = getPortalContext();
  const branding = getTechBranding(portal.companyId);

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setSubmitting(true);

    try {
      if (!isSupabaseConfigured()) {
        throw new Error('Supabase is not configured.');
      }

      const redirectTo = `${window.location.origin}/reset-password`;
      const { error: resetErr } = await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo });
      if (resetErr) throw resetErr;

      setSuccessMessage(
        "If an account exists for this email, you'll receive a password reset link shortly."
      );
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl animate-pulse" />
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-white/20">
            <div className="text-center mb-8">
              <div className="mb-4 flex items-center justify-center">
                <div className="w-28 h-16 flex items-center justify-center">
                  {logoError ? (
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-gradient rounded-2xl">
                      <Shield className="w-8 h-8 text-white" />
                    </div>
                  ) : (
                    <img
                      src={branding.logoPath}
                      alt={`${branding.displayName} logo`}
                      className="max-h-16 w-auto object-contain"
                      onError={() => setLogoError(true)}
                    />
                  )}
                </div>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{branding.displayName} Technician Portal</h1>
              <p className="text-gray-600">Sign in to enroll a customer</p>
            </div>

            {!isSupabaseConfigured() && (
              <div className="mb-6 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-2xl">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-yellow-800 mb-1">Setup Required</p>
                    <p className="text-sm text-yellow-700">
                      Configure Supabase env vars to enable technician authentication.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="mb-6 p-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <p className="text-sm text-red-800 font-medium">{error}</p>
                </div>
              </div>
            )}

            {successMessage && (
              <div className="mb-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-green-600" />
                  <p className="text-sm text-green-900 font-medium">{successMessage}</p>
                </div>
              </div>
            )}

            <form onSubmit={mode === 'login' ? handleSubmit : handleForgotPassword} className="space-y-6">
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-gray-900 placeholder-gray-500 bg-gray-50 hover:bg-white"
                    placeholder="tech@company.com"
                    autoComplete="email"
                    required
                    disabled={submitting}
                  />
                </div>

                {mode === 'login' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">Password</label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-gray-900 placeholder-gray-500 bg-gray-50 hover:bg-white"
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      required
                      disabled={submitting}
                    />
                  </div>
                )}

                {mode === 'login' ? (
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 accent-brand-primary"
                      checked={rememberDevice}
                      onChange={(e) => setRememberDevice(e.target.checked)}
                      disabled={submitting}
                    />
                    Remember this device
                  </label>
                ) : null}
              </div>

              <button
                type="submit"
                className="w-full bg-brand-gradient hover:brightness-110 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-200 transform hover:scale-[1.02] shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                disabled={submitting}
              >
                <div className="flex items-center justify-center space-x-2">
                  {submitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>{mode === 'login' ? 'Signing you in...' : 'Sending reset link...'}</span>
                    </>
                  ) : (
                    <>
                      {mode === 'login' ? <LogIn className="w-5 h-5" /> : null}
                      <span>{mode === 'login' ? 'Access Portal' : 'Send Reset Link'}</span>
                    </>
                  )}
                </div>
              </button>

              <div className="flex items-center justify-between text-sm">
                {mode === 'login' ? (
                  <button
                    type="button"
                    onClick={() => {
                      setMode('forgot');
                      setError(null);
                      setSuccessMessage(null);
                      setPassword('');
                    }}
                    className="text-brand-primary hover:opacity-90 font-semibold"
                    disabled={submitting}
                  >
                    Forgot password?
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setMode('login');
                      setError(null);
                      setSuccessMessage(null);
                    }}
                    className="text-brand-primary hover:opacity-90 font-semibold"
                    disabled={submitting}
                  >
                    Back to sign in
                  </button>
                )}

                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="text-gray-600 hover:text-gray-800"
                  disabled={submitting}
                >
                  Home
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
